import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, Loader2 } from "lucide-react";
import { APPOINTMENT_CATEGORIES, CATEGORY_LABEL } from "@/lib/categories";

export default function TypesTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/appointment-types");
            setItems(r.data || []);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const remove = async (t) => {
        if (!window.confirm(`Supprimer "${t.name}" ?`)) return;
        try {
            await api.delete(`/admin/appointment-types/${t.id}`);
            toast.success("Supprimé");
            load();
        } catch { toast.error("Échec"); }
    };

    return (
        <div data-testid="admin-types">
            <div className="flex justify-between items-end mb-6">
                <div>
                    <h2 className="font-display text-2xl text-brand-ink">Types de rendez-vous</h2>
                    <p className="font-sans text-sm text-brand-ink/60 mt-1">Vaccins, consultations, mesures…</p>
                </div>
                <button onClick={() => setCreating(true)} className="ll-btn ll-btn-primary">
                    <Plus className="h-4 w-4" /> Ajouter
                </button>
            </div>

            {loading ? <Loader2 className="h-6 w-6 animate-spin text-brand-primary" /> :
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((t) => (
                        <div key={t.id} className="bg-white border border-brand-line p-5">
                            <div className="flex items-center justify-between mb-3">
                                <span className="h-3 w-3 rounded-full" style={{background: t.color}} />
                                <div className="flex gap-2">
                                    <button onClick={() => setEditing(t)} className="text-brand-primary"><Edit2 className="h-4 w-4" /></button>
                                    <button onClick={() => remove(t)} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                                </div>
                            </div>
                            <h3 className="font-display text-lg text-brand-ink mb-1">{t.name}</h3>
                            <p className="text-xs text-brand-ink/55 font-sans mb-3">{t.duration_minutes} min{t.requires_birthdate && " · date de naissance requise"}</p>
                            {t.description && <p className="font-sans text-sm text-brand-ink/65">{t.description}</p>}
                            <div className="mt-4 flex flex-wrap items-center gap-2">
                                <span className={`text-xs px-2 py-0.5 rounded ${t.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                                    {t.active ? "Actif" : "Inactif"}
                                </span>
                                {t.category && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-brand-mint/40 text-brand-primary">
                                        {CATEGORY_LABEL[t.category] || t.category}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            }

            {(editing || creating) && (
                <TypeDialog item={editing} onClose={() => { setEditing(null); setCreating(false); }} onSaved={() => { setEditing(null); setCreating(false); load(); }} />
            )}
        </div>
    );
}

function TypeDialog({ item, onClose, onSaved }) {
    const isEdit = !!item;
    const [form, setForm] = useState(item || {
        name: "",
        duration_minutes: 15,
        color: "#4e6d6f",
        description: "",
        active: true,
        requires_birthdate: false,
        category: "",
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                duration_minutes: parseInt(form.duration_minutes, 10),
                color: form.color,
                description: form.description || null,
                active: !!form.active,
                requires_birthdate: !!form.requires_birthdate,
                category: form.category || null,
            };
            if (isEdit) await api.put(`/admin/appointment-types/${item.id}`, payload);
            else await api.post(`/admin/appointment-types`, payload);
            toast.success("Enregistré");
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-brand-ink/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white max-w-md w-full">
                <div className="flex justify-between items-center p-5 border-b border-brand-line">
                    <h3 className="font-display text-xl">{isEdit ? "Modifier" : "Nouveau type"}</h3>
                    <button onClick={onClose}><X className="h-5 w-5" /></button>
                </div>
                <div className="p-6 space-y-4 font-sans">
                    <F label="Nom"><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="ll-input" /></F>
                    <F label="Catégorie">
                        <select
                            value={form.category || ""}
                            onChange={e => setForm({...form, category: e.target.value})}
                            className="ll-input"
                            data-testid="type-category-select"
                        >
                            <option value="">— Sans catégorie —</option>
                            {APPOINTMENT_CATEGORIES.map((c) => (
                                <option key={c.key} value={c.key}>{c.label}</option>
                            ))}
                        </select>
                    </F>
                    <F label="Durée (minutes)"><input type="number" min="5" max="240" value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: e.target.value})} className="ll-input" /></F>
                    <F label="Couleur"><input type="color" value={form.color} onChange={e => setForm({...form, color: e.target.value})} className="h-10 w-20" /></F>
                    <F label="Description"><textarea rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="ll-input resize-none" /></F>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} />
                        Actif (visible côté client)
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.requires_birthdate} onChange={e => setForm({...form, requires_birthdate: e.target.checked})} />
                        Date de naissance requise (vaccins…)
                    </label>
                </div>
                <div className="flex justify-end gap-3 p-5 border-t border-brand-line">
                    <button onClick={onClose} className="ll-btn ll-btn-ghost">Annuler</button>
                    <button onClick={save} disabled={saving} className="ll-btn ll-btn-primary disabled:opacity-60">
                        {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> …</> : "Enregistrer"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const F = ({ label, children }) => (
    <div>
        <label className="ll-eyebrow text-brand-ink/60 block mb-1">{label}</label>
        {children}
    </div>
);
